import * as z from "zod";
import { defineMutators } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { syncAllFacets } from "../commitFiles";
import { ensureWritable } from "../copyOnWrite";
import {
  isValidStepTestName,
  STEP_TEST_CASES_DIR,
} from "../stepTests/constants";
import { mutator } from "./mutator";
import {
  deleteStepTestClockFile,
  deleteStepTestRequestsFile,
  deleteStepTestVolumeFixtureFiles,
} from "./stepTestCommitFiles";
import { branchSchema, insertUserAction } from "./utils";

const unpinStepTestSchema = z
  .object({
    workflowId: z.string(),
    stepDir: z.string(),
    name: z.string(),
  })
  .merge(branchSchema);

function createUnpinStepTest(recordUserAction: boolean) {
  return mutator({
    schema: unpinStepTestSchema,
    resolve: {
      branch: { kind: "branch", from: "branchId" },
    },
    permissions: [
      {
        permission: "space:write",
        scopeId: ({ args }) => args.branch.workflow.spaceId,
      },
    ],
    fn: async ({ tx, ctx, args }) => {
      const {
        branch,
        stepDir,
        name,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      } = args;
      if (!isValidStepTestName(name)) {
        throw userFacingError(
          "Step test names may only contain letters, numbers, dashes, and underscores"
        );
      }
      const { commitId } = await ensureWritable(
        tx,
        ctx,
        ctx.tenantId,
        {
          branchId: branch.id,
          forkBranchId,
          forkCommitId,
          forkChatId,
          workingCommitId,
          workingChatId,
        },
        branch
      );
      const base = `${stepDir}/${STEP_TEST_CASES_DIR}/${name}`;
      await tx.mutate.commitFiles.delete({ commitId, path: `${base}.input` });
      await tx.mutate.commitFiles.delete({ commitId, path: `${base}.output` });
      await tx.mutate.commitFiles.delete({ commitId, path: `${base}.meta` });
      await deleteStepTestClockFile({ tx, commitId, stepDir, name });
      await deleteStepTestRequestsFile({ tx, commitId, stepDir, name });
      await deleteStepTestVolumeFixtureFiles({ tx, commitId, stepDir, name });
      await syncAllFacets({ tx, commitId, tenantId: ctx.tenantId });
      if (recordUserAction) {
        await insertUserAction(
          tx,
          commitId,
          ctx,
          ctx.tenantId,
          "unpinStepTest",
          {
            stepDir,
            name,
          }
        );
      }
    },
  });
}

export const unpinStepTest = createUnpinStepTest(true);
export const { unpinStepTestWithoutUserAction } = defineMutators({
  unpinStepTestWithoutUserAction: createUnpinStepTest(false),
});
